(() => {
  if (window.DDSectionAnchors) return;

  const normalize = () => {
    const counts = new Map();
    const sections = document.querySelectorAll('[data-dd-section-anchor]');
    const reserved = new Set(Array.from(sections, (section) => section.dataset.ddSectionAnchor));
    const assigned = new Set();

    sections.forEach((section) => {
      const base = section.dataset.ddSectionAnchor;
      const count = (counts.get(base) || 0) + 1;
      counts.set(base, count);

      if (count === 1) {
        section.id = base;
        assigned.add(base);
        section.removeAttribute('data-dd-section-anchor-duplicate');
        return;
      }

      const suffix = section.dataset.ddSectionId || count;
      const candidate = `${base}-${suffix}`;
      let id = candidate;
      let disambiguator = 2;
      while (reserved.has(id) || assigned.has(id)) {
        id = `${candidate}-${disambiguator++}`;
      }
      section.id = id;
      assigned.add(id);
      section.setAttribute('data-dd-section-anchor-duplicate', String(count));
      console.warn(`[Desert Dust] Duplicate section anchor "${base}" detected; using "${section.id}" for the later section.`);
    });
  };

  const targetSelector = '[data-dd-section-anchor], [data-dd-ux-size-anchor]';
  const stickySelector = '.dd-ux-size-nav-section, .shopify-section.dd-section-11:has(> .dd-hotlinks-shell--sticky)';
  let alignment;
  let userInteracted = false;
  let nativeNavigation;

  const findTarget = (hash = window.location.hash) => {
    if (!hash) return null;
    let id;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (_error) {
      return null;
    }
    const target = document.getElementById(id);
    return target?.matches(targetSelector) && target.getClientRects().length ? target : null;
  };

  const targetOffset = (target, predictedHeaderHeight) => {
    const rootStyle = window.getComputedStyle(document.documentElement);
    const visibleHeight = Number.parseFloat(rootStyle.getPropertyValue('--dd-header-visible-height'));
    const headerHeight = Number.parseFloat(rootStyle.getPropertyValue('--dd-header-height')) || 0;
    const currentHeaderHeight = Number.isFinite(visibleHeight) ? visibleHeight : headerHeight;
    const headerOffset = predictedHeaderHeight ?? currentHeaderHeight;
    let offset = headerOffset;

    document.querySelectorAll(stickySelector).forEach((bar) => {
      if (bar.contains(target) || target.contains(bar) || !(bar.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
      const style = window.getComputedStyle(bar);
      const rect = bar.getBoundingClientRect();
      if (style.position !== 'sticky' || style.visibility === 'hidden' || !rect.height) return;
      const stickyTop = Number.parseFloat(style.top);
      offset = Math.max(offset, (Number.isFinite(stickyTop) ? stickyTop + headerOffset - currentHeaderHeight : headerOffset) + rect.height);
    });

    return offset;
  };

  const releaseNativeNavigation = () => {
    if (!nativeNavigation) return;
    window.cancelAnimationFrame(nativeNavigation.frame);
    nativeNavigation.header?.endAnchorNavigation();
    nativeNavigation.header = null;
  };

  const cancelNativeNavigation = () => {
    if (!nativeNavigation) return;
    releaseNativeNavigation();
    window.clearTimeout(nativeNavigation.timer);
    nativeNavigation = undefined;
  };

  const cancelAlignment = () => {
    if (!alignment) return;
    window.cancelAnimationFrame(alignment.frame);
    alignment.timers.forEach((timer) => window.clearTimeout(timer));
    alignment = undefined;
  };

  const correctAlignment = () => {
    if (!alignment || findTarget() !== alignment.target) return;
    const target = alignment.target;
    const offset = targetOffset(target);
    target.style.scrollMarginTop = `${offset}px`;
    const top = Math.max(0, Math.min(
      target.getBoundingClientRect().top + window.scrollY - offset,
      document.documentElement.scrollHeight - window.innerHeight
    ));
    if (Math.abs(top - window.scrollY) > 1) {
      window.scrollTo({ top, behavior: 'instant' });
    }
  };

  const alignHashTarget = () => {
    cancelAlignment();
    const target = findTarget();
    if (!target) return;
    alignment = { target, timers: [], frame: 0 };
    alignment.frame = window.requestAnimationFrame(() => {
      if (!alignment) return;
      alignment.frame = window.requestAnimationFrame(correctAlignment);
    });
    alignment.timers = [120, 350, 700].map((delay) => window.setTimeout(correctAlignment, delay));
    alignment.timers.push(window.setTimeout(cancelAlignment, 800));
  };

  const normalizeAndAlign = () => {
    normalize();
    alignHashTarget();
  };

  const initialize = () => {
    normalize();
    if (!userInteracted) alignHashTarget();
  };

  const interruptAlignment = () => {
    userInteracted = true;
    cancelAlignment();
    // Release header control immediately, but still consume the click's queued
    // hashchange so it cannot restart alignment after the user scrolls away.
    releaseNativeNavigation();
  };

  ['wheel', 'touchstart', 'pointerdown'].forEach((event) => {
    window.addEventListener(event, interruptAlignment, { passive: true });
  });
  window.addEventListener('keydown', (event) => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab', 'Escape'].includes(event.key)) {
      interruptAlignment();
    }
  });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return;
    const target = findTarget(url.hash);
    if (!target) return;
    userInteracted = true;
    cancelAlignment();
    cancelNativeNavigation();

    const destination = Math.max(0, Math.min(
      target.getBoundingClientRect().top + window.scrollY - targetOffset(target, 0),
      document.documentElement.scrollHeight - window.innerHeight
    ));
    const header = document.querySelector('sticky-header');
    const preparedHeader = header?.beginAnchorNavigation?.(destination) ? header : null;
    target.style.scrollMarginTop = `${targetOffset(target)}px`;

    // Native anchors retain history and focus. Their hashchange must not start a
    // second scroll; load/deep-link alignment remains a separate lifecycle.
    const navigation = { fromURL: window.location.href, toURL: url.href, hashHandled: false, header: preparedHeader, frame: 0, timer: 0 };
    nativeNavigation = navigation;
    navigation.frame = window.requestAnimationFrame(() => {
      if (nativeNavigation !== navigation) return;
      navigation.frame = window.requestAnimationFrame(() => {
        if (nativeNavigation === navigation) releaseNativeNavigation();
      });
    });
    navigation.timer = window.setTimeout(() => {
      if (nativeNavigation === navigation) cancelNativeNavigation();
    }, 1000);
  });

  document.fonts?.ready.then(correctAlignment);

  let normalizeFrame;
  const normalizeAfterSectionMutation = () => {
    window.cancelAnimationFrame(normalizeFrame);
    normalizeFrame = window.requestAnimationFrame(() => {
      normalizeFrame = undefined;
      normalize();
    });
  };

  window.DDSectionAnchors = { normalize, alignHashTarget };
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
  window.addEventListener('load', initialize, { once: true });
  window.addEventListener('hashchange', (event) => {
    if (nativeNavigation && !nativeNavigation.hashHandled &&
        event.oldURL === nativeNavigation.fromURL && event.newURL === nativeNavigation.toURL) {
      nativeNavigation.hashHandled = true;
      normalize();
      return;
    }
    cancelNativeNavigation();
    normalizeAndAlign();
  });
  document.addEventListener('shopify:section:load', normalize);
  document.addEventListener('shopify:section:unload', normalizeAfterSectionMutation);
  document.addEventListener('shopify:section:reorder', normalizeAfterSectionMutation);

  if (document.readyState !== 'loading') initialize();
})();
